import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Edit3 } from 'lucide-react';
import { Project, Task, TimeEntry, User, Client, NewProject } from '../../../types';
import TaskManagement from '../TaskManagement';
import TaskDetail from '../TaskDetail/TaskDetail';
import TimeEntryForm from '../../timeTracking/TimeEntryForm';
import { ProjectForm } from '../ProjectForm/ProjectForm';
import ProjectService from '../../../services/projectService';
import UserService from '../../../services/userService';
import { useAuth } from '../../../hooks';
import api from '../../../services/api';
import { Button, Card, LoadingSpinner } from '../../common';

// Overview Tab Component
const ProjectOverview: React.FC<{ 
  project: Project; 
  tasks: Task[]; 
  timeEntries: TimeEntry[]; 
}> = ({ project, tasks, timeEntries }) => {
  const totalHours = timeEntries.reduce((sum, entry) => sum + entry.hours, 0);
  const billableHours = timeEntries.filter(entry => entry.billable).reduce((sum, entry) => sum + entry.hours, 0);
  const totalRevenue = timeEntries.filter(entry => entry.billable).reduce((sum, entry) => sum + (entry.hours * (entry.hourlyRate || 0)), 0);
  
  const completedTasks = tasks.filter(task => task.status === 'completed').length;
  const totalTasks = tasks.length;

  return (
    <div className="space-y-6">
      {/* Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="text-center">
          <h4 className="text-sm font-medium text-gray-500 mb-2">Total Hours</h4>
          <div className="text-3xl font-bold text-blue-600">{totalHours.toFixed(1)}</div>
        </Card>

        <Card className="text-center">
          <h4 className="text-sm font-medium text-gray-500 mb-2">Billable Hours</h4>
          <div className="text-3xl font-bold text-green-600">{billableHours.toFixed(1)}</div>
        </Card>

        <Card className="text-center">
          <h4 className="text-sm font-medium text-gray-500 mb-2">Revenue</h4>
          <div className="text-3xl font-bold text-green-600">${totalRevenue.toFixed(2)}</div>
        </Card>

        <Card className="text-center">
          <h4 className="text-sm font-medium text-gray-500 mb-2">Tasks Completed</h4>
          <div className="text-3xl font-bold text-purple-600">{completedTasks}/{totalTasks}</div>
        </Card>
      </div>

      {/* Project Details and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h4 className="text-lg font-semibold mb-4">Project Details</h4>
          <div className="space-y-3">
            {project.startDate && (
              <div>
                <span className="font-medium text-gray-700">Start Date:</span>
                <span className="ml-2 text-gray-600">{new Date(project.startDate).toLocaleDateString()}</span>
              </div>
            )}
            {project.dueDate && (
              <div>
                <span className="font-medium text-gray-700">Due Date:</span>
                <span className="ml-2 text-gray-600">{new Date(project.dueDate).toLocaleDateString()}</span>
              </div>
            )}
            {project.budget && (
              <div>
                <span className="font-medium text-gray-700">Budget:</span>
                <span className="ml-2 text-gray-600">${Number(project.budget).toFixed(2)}</span>
              </div>
            )}
          </div>
        </Card>

        <Card>
          <h4 className="text-lg font-semibold mb-4">Recent Activity</h4>
          <div className="space-y-3 max-h-48 overflow-y-auto">
            {timeEntries.slice(0, 5).map(entry => (
              <div key={entry.id} className="p-3 bg-gray-50 rounded border">
                <div className="font-medium text-sm">User ID: {entry.userId}</div>
                <div className="text-sm text-gray-600">{entry.hours}h - {entry.description}</div>
                <div className="text-xs text-gray-500">{entry.date ? new Date(entry.date).toLocaleDateString() : 'No date'}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

// Time Tracking Tab Component
const TimeTracking: React.FC<{
  projectId: number;
  timeEntries: TimeEntry[];
  tasks: Task[];
  users: User[];
  onTimeEntriesChange: (entries: TimeEntry[]) => void;
}> = ({ projectId, timeEntries, tasks, users, onTimeEntriesChange }) => {
  const [showForm, setShowForm] = useState(false);

  const totalHours = timeEntries.reduce((sum, entry) => sum + entry.hours, 0);
  const billableHours = timeEntries.filter(entry => entry.billable).reduce((sum, entry) => sum + entry.hours, 0);
  const totalRevenue = timeEntries.filter(entry => entry.billable).reduce((sum, entry) => sum + (entry.hours * (entry.hourlyRate || 0)), 0);

  const handleTimeEntrySubmit = async () => {
    // Reload time entries after form submission
    const updatedEntries = await ProjectService.getProjectTimeEntries(projectId);
    onTimeEntriesChange(updatedEntries);
    setShowForm(false);
  };

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="text-center">
          <h4 className="text-sm font-medium text-gray-500 mb-2">Total Hours</h4>
          <div className="text-2xl font-bold text-blue-600">{totalHours.toFixed(1)}</div>
        </Card>
        <Card className="text-center">
          <h4 className="text-sm font-medium text-gray-500 mb-2">Billable Hours</h4>
          <div className="text-2xl font-bold text-green-600">{billableHours.toFixed(1)}</div>
        </Card>
        <Card className="text-center">
          <h4 className="text-sm font-medium text-gray-500 mb-2">Total Revenue</h4>
          <div className="text-2xl font-bold text-green-600">${totalRevenue.toFixed(2)}</div>
        </Card>
      </div>

      {/* Add Time Entry Button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Time Entries</h3>
        <Button
          variant="primary"
          size="md"
          onClick={() => setShowForm(!showForm)}
        >
          Add Time Entry
        </Button>
      </div>

      {/* Time Entry Form */}
      {showForm && (
        <div className="bg-white p-6 rounded-lg shadow border">
          <TimeEntryForm
            projectId={projectId}
            onSubmit={handleTimeEntrySubmit}
            onClose={() => setShowForm(false)}
          />
        </div>
      )}

      {/* Time Entries Table */}
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Billable</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {timeEntries.map(entry => (
                <tr key={entry.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {entry.date ? new Date(entry.date).toLocaleDateString() : 'No date'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div>{entry.description}</div>
                    {entry.taskId && (
                      <div className="text-xs text-gray-500">Task ID: {entry.taskId}</div>
                    )}
                    <div className="text-xs text-gray-500">
                      User ID: {entry.userId}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.hours}h</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${entry.hourlyRate || 0}/hr</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      entry.billable 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {entry.billable ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    ${entry.billable ? (entry.hours * (entry.hourlyRate || 0)).toFixed(2) : '0.00'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Team Management Tab Component  
const TeamManagement: React.FC<{ project: Project; users: User[] }> = ({ project, users }) => {
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Team Management</h3>
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-yellow-800">Team management functionality can be implemented here, including:</p>
        <ul className="mt-2 text-yellow-700 list-disc list-inside">
          <li>Assign team members to projects</li>
          <li>Set role permissions</li>
          <li>Track individual performance</li>
          <li>Manage workload distribution</li>
        </ul>
      </div>
      
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-medium">Available Team Members</h4>
        </div>
        <div className="divide-y divide-gray-200">
          {users.map(user => (
            <div key={user.id} className="px-6 py-4 flex justify-between items-center">
              <div>
                <div className="font-medium text-gray-900">{user.first_name} {user.last_name}</div>
                <div className="text-sm text-gray-500">{user.email}</div>
                <div className="text-sm text-gray-500">Role: {user.roles?.[0]?.name || 'User'}</div>
              </div>
              <Button variant="primary" size="sm">
                Assign to Project
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Main ProjectDetail Component
interface ProjectDetailProps {
  project: Project;
  onBack: () => void;
}

const ProjectDetail: React.FC<ProjectDetailProps> = ({ project, onBack }) => {
  const [projectData, setProjectData] = useState<Project>(project);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const { token, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (project?.id) {
      loadProjectData(project.id);
    }
  }, [project.id]);

  // Handle task query parameter - open task detail when specified in URL
  useEffect(() => {
    const taskId = searchParams.get('task');
    if (taskId && tasks.length > 0) {
      const task = tasks.find(t => t.id === parseInt(taskId));
      if (task) {
        setSelectedTask(task);
        // Clear the query parameter after opening
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, tasks, setSearchParams]);

  const loadProjectData = async (projectId: number) => {
    try {
      setLoading(true);
      setError(null);
      
      const [projectData, tasksData, timeEntriesData, usersData, clientsResponse] = await Promise.all([
        ProjectService.getProject(projectId),
        ProjectService.getProjectTasks(projectId),
        ProjectService.getProjectTimeEntries(projectId),
        UserService.getUsers(),
        api.get<any>('/api/clients', token || undefined)
      ]);

      // Transform clients data with defensive programming
      const clientsArrayRaw = Array.isArray(clientsResponse) ? clientsResponse :
                             (clientsResponse && Array.isArray(clientsResponse.clients)) ? clientsResponse.clients : [];
      const clientsArray = clientsArrayRaw.map((client: any) => ({
        ...client,
        name: client.company_name || client.name
      }));

      // Ensure all data is valid before setting state
      setProjectData(projectData || null);
      setTasks(Array.isArray(tasksData) ? tasksData : []);
      setTimeEntries(Array.isArray(timeEntriesData) ? timeEntriesData : []);
      setUsers(Array.isArray(usersData) ? usersData : []);
      setClients(clientsArray);
    } catch (error) {
      console.error('Error loading project data:', error);
      setError('Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProject = async (formData: NewProject) => {
    try {
      setUpdating(true);
      setError(null);

      // The backend returns project with snake_case fields, so use client_id not clientId
      const clientId = (projectData as any).client_id || projectData.clientId;

      const backendData = {
        // Don't allow client changes for existing projects
        client_id: clientId,
        name: formData.name,
        description: formData.description || '',
        project_type: null,
        status: formData.status,
        priority: 'medium',
        start_date: formData.startDate || null,
        end_date: formData.dueDate || null,
        budget: (formData.budget || 0) > 0 ? formData.budget : null,
        estimated_hours: (formData.estimatedHours || 0) > 0 ? formData.estimatedHours : null,
        project_manager_id: null,
        notes: ''
      };

      console.log('Updating project with data:', backendData);

      // Use the actual project ID from component state
      await api.put<any>(`/api/projects/${projectData.id}`, backendData, token || undefined);

      console.log('Project updated successfully');
      setShowEditForm(false);

      // Reload project data to get updated information
      await loadProjectData(projectData.id);
    } catch (error: any) {
      console.error('Failed to update project:', error);

      let errorMessage = 'Failed to update project. Please try again.';

      // Extract more specific error message if available
      if (error.details) {
        errorMessage = `Validation Error: ${error.details}`;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      // Don't close the form on error so user can see what went wrong
    } finally {
      setUpdating(false);
    }
  };

  const handleSelectTask = (task: Task) => {
    setSelectedTask(task);
  };

  const handleBackToTasks = () => {
    setSelectedTask(null);
  };

  const handleEditTask = (task: Task) => {
    setSelectedTask(null);
  };

  const handleDeleteTask = async (taskId: number) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      await api.delete(`/api/projects/${projectData.id}/tasks/${taskId}`, token || undefined);
      // Refresh tasks and go back to task list
      await loadProjectData(projectData.id);
      setSelectedTask(null);
    } catch (error) {
      console.error('Failed to delete task:', error);
      alert('Failed to delete task. Please try again.');
    }
  };

  // If a task is selected, show task detail view
  if (selectedTask) {
    return (
      <TaskDetail
        task={selectedTask}
        onBack={handleBackToTasks}
        onEdit={handleEditTask}
        onDelete={handleDeleteTask}
        users={users}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <LoadingSpinner message="Loading project..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-yellow-800">Project not found</p>
      </div>
    );
  }

  return (
    <>
      <TaskManagement
        projectId={projectData.id}
        projectName={projectData.name}
        onSelectTask={handleSelectTask}
        onBack={onBack}
        onEditProject={() => setShowEditForm(true)}
      />

      {/* Edit Project Form */}
      {showEditForm && (
        <ProjectForm
          project={projectData}
          onClose={() => {
            setShowEditForm(false);
            setError(null);
          }}
          onSubmit={handleUpdateProject}
          loading={updating}
          clients={clients}
          error={error}
        />
      )}
    </>
  );
};

export default ProjectDetail;